import { BaseState, FavoriteView, Filter, INITIAL_BASE_STATE, Pagination, TagCategory } from '@iot-platform/models/common';
import { Device } from '@iot-platform/models/i4b';
import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { DevicesActions } from '../actions';
import { DataGuardCheckStatus } from '@iot-platform/models/data-guard';

export interface State extends BaseState<Device, Pagination, Filter> {
  currentFavoriteView: FavoriteView | null;
  tags: TagCategory[];
  tagsLoading: boolean;
  tagsLoaded: boolean;
  dataGuardStatus: DataGuardCheckStatus | null;
  progress: number | null;
}

export const adapter: EntityAdapter<Device> = createEntityAdapter<Device>();

export const initialState: State = adapter.getInitialState({
  ...INITIAL_BASE_STATE,
  currentFavoriteView: null,
  tags: [],
  tagsLoading: false,
  tagsLoaded: false,
  dataGuardStatus: null,
  progress: null
});

export const devicesFeature = createFeature({
  name: 'devices',
  reducer: createReducer(
    initialState,
    on(DevicesActions.loadDeviceTags, DevicesActions.updateDeviceTags, (state: State): State => ({ ...state, tagsLoading: true, tagsLoaded: false })),
    on(
      DevicesActions.loadDeviceTagsSuccess,
      DevicesActions.updateDeviceTagsSuccess,
      (state: State, { tags }): State => ({ ...state, tags, tagsLoading: false, tagsLoaded: true })
    ),
    on(
      DevicesActions.loadDeviceTagsFailure,
      DevicesActions.updateDeviceTagsFailure,
      (state: State): State => ({ ...state, tagsLoading: false, tagsLoaded: false })
    ),
    on(
      DevicesActions.loadDeviceByIdSuccess,
      DevicesActions.getOneSilentlySuccess,
      (state: State, { device }): State => adapter.setOne(device, { ...state, selectedId: device.id })
    ),
    on(
      DevicesActions.updateDeviceSuccess,
      DevicesActions.activateDeviceSuccess,
      DevicesActions.resetDeviceSuccess,
      (state: State, { device }): State => adapter.updateOne({ id: device.id, changes: { ...device } }, { ...state, selectedId: device.id })
    ),
    on(DevicesActions.moveDevicesSuccess, (state: State, { devices }): State => {
      const updatedState = adapter.updateMany(
        devices.map((device) => ({ id: device.id, changes: { ...device } })),
        state
      );
      return {
        ...updatedState,
        selectedId: devices.some((device) => device.id === state.selectedId) ? state.selectedId : null,
        progress: null
      };
    }),
    on(DevicesActions.updateBulkProgress, (state: State, { progress }): State => ({ ...state, progress })),
    on(DevicesActions.moveDevicesFailure, (state: State): State => ({ ...state, progress: null })),
    on(DevicesActions.selectDeviceSuccess, (state: State, { device }): State => ({ ...state, selectedId: device.id })),
    on(
      DevicesActions.saveCurrentFavoriteView,
      (state: State, { currentFavoriteView }): State => ({
        ...state,
        currentFavoriteView,
        filters: currentFavoriteView?.filters ?? []
      })
    ),
    on(DevicesActions.loadDataGuardStatusSuccess, (state: State, { status }): State => ({ ...state, dataGuardStatus: status })),

    /* ------------- */
    //      UI
    /* ------------- */
    on(
      DevicesActions.loadDeviceById,
      DevicesActions.updateDevice,
      DevicesActions.deleteDevice,
      DevicesActions.activateDevice,
      DevicesActions.resetDevice,
      DevicesActions.sendCommand,
      DevicesActions.moveDevices,
      (state: State): State => ({ ...state, loading: true, loaded: false })
    ),
    on(
      DevicesActions.loadDeviceByIdSuccess,
      DevicesActions.updateDeviceSuccess,
      DevicesActions.deleteDeviceSuccess,
      DevicesActions.activateDeviceSuccess,
      DevicesActions.resetDeviceSuccess,
      DevicesActions.sendCommandSuccess,
      DevicesActions.moveDevicesSuccess,
      (state: State): State => ({ ...state, loading: false, loaded: true })
    ),
    on(
      DevicesActions.loadDeviceByIdFailure,
      DevicesActions.updateDeviceFailure,
      DevicesActions.deleteDeviceFailure,
      DevicesActions.activateDeviceFailure,
      DevicesActions.resetDeviceFailure,
      DevicesActions.sendCommandFailure,
      DevicesActions.moveDevicesFailure,
      (state: State): State => ({ ...state, loading: false, loaded: false })
    )
  )
});
